import { useState, useEffect } from "react";

/**
 * Small inline "save / load" widget for Rx Notes templates.
 * Persists to localStorage under `glpflo_rx_note_tpl` (base64-obfuscated).
 * Used inside the prescriber's ERX action modal so providers can reuse
 * common-note snippets across patients without leaving the screen.
 */
export default function RxNoteSaver({ value, onSelect }) {
  const [saved, setSaved] = useState([]);
  const [open, setOpen] = useState(false);
  const STORAGE_KEY = "glpflo_rx_note_tpl";

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setSaved(JSON.parse(atob(raw)));
    } catch { setSaved([]); }
  }, []);

  const persist = (data) => {
    localStorage.setItem(STORAGE_KEY, btoa(JSON.stringify(data)));
    setSaved(data);
  };

  const handleSave = () => {
    if (!value || !value.trim()) return;
    const label = value.trim().slice(0, 50);
    persist([...saved.filter(s => s.text !== value.trim()), { label, text: value.trim() }]);
  };

  const handleDelete = (idx) => {
    persist(saved.filter((_, i) => i !== idx));
  };

  return (
    <div className="relative" data-testid="rx-note-saver">
      <div className="flex items-center gap-2">
        {value && value.trim() && (
          <button onClick={handleSave} className="text-[10px] text-purple-600 hover:text-purple-700 font-medium" data-testid="save-note-tpl-btn">
            Save as template
          </button>
        )}
        {saved.length > 0 && (
          <button onClick={() => setOpen(!open)} className="text-[10px] text-emerald-600 hover:text-emerald-700 font-medium" data-testid="load-note-tpl-btn">
            {open ? "Close" : `Load saved (${saved.length})`}
          </button>
        )}
      </div>
      {open && saved.length > 0 && (
        <div className="absolute right-0 top-6 z-50 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-lg p-2 w-72 max-h-48 overflow-y-auto" data-testid="note-tpl-dropdown">
          {saved.map((s, i) => (
            <div key={s.label + i} className="flex items-center justify-between gap-2 p-2 hover:bg-slate-50 dark:hover:bg-slate-800/50 dark:bg-slate-800/40 rounded-lg cursor-pointer group">
              <button onClick={() => { onSelect(s.text); setOpen(false); }} className="text-left text-xs text-slate-600 dark:text-slate-300 flex-1 truncate" data-testid={`note-tpl-${i}`}>
                {s.label}
              </button>
              <button onClick={() => handleDelete(i)} className="text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 text-xs p-0.5">x</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
